/**
 * MfaEnrollmentPanel — fluxo de enrollment TOTP em 3 etapas internas.
 *
 * Renderiza dentro de um SlideOverPanel (Sprint 6 do plano):
 *   1. QR code + secret base32 (entrada manual fallback)
 *   2. Confirmacao do primeiro codigo TOTP
 *   3. 8 recovery codes exibidos UMA UNICA vez — checkbox "salvei" libera fechar.
 *
 * Usado em:
 *   - Perfil do proprio usuario (`/perfil`)
 *   - Ficha do usuario para forcar enrollment (`/acessos/usuarios/:id`)
 *
 * Props:
 *  - `open` (boolean)
 *  - `onClose()` — fecha o painel
 *  - `onCompleted()` — callback apos enrollment bem-sucedido
 */
import React, { useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiCheckCircle, FiCopy } from "react-icons/fi";

import SlideOverPanel from "../SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  fetchMfaSaude,
  postMfaSetupSecret,
  postMfaVerifyAndEnable,
} from "../../pages/Acessos/api";

const ETAPA_QR = 0;
const ETAPA_VERIFICAR = 1;
const ETAPA_RECOVERY = 2;

const QrImg = styled.img`
  display: block;
  margin: 0 auto;
  width: 220px;
  height: 220px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 8px;
  background: #fff;
`;

const SecretBox = styled.code`
  display: block;
  padding: 8px 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  font-family: var(--font-mono, ui-monospace, monospace);
  letter-spacing: 0.1em;
  word-break: break-all;
  text-align: center;
`;

const CodeInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 1.4rem;
  text-align: center;
  letter-spacing: 0.4em;
  font-family: var(--font-mono, ui-monospace, monospace);
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  border-radius: 10px;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
  }
`;

const RecoveryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 8px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.95rem;
`;

const Step = styled.p`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 4px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  background: ${({ theme }) => theme.accent};
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.85rem;
  cursor: pointer;
`;

const ErrorMsg = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.dangerSurface};
  color: ${({ theme }) => theme.dangerText};
  border: 1px solid ${({ theme }) => theme.dangerBorder};
  font-size: 0.85rem;
`;

const Warning = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.warningSurface};
  color: ${({ theme }) => theme.warningText};
  border: 1px solid ${({ theme }) => theme.warningBorder};
  font-size: 0.85rem;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

function MfaEnrollmentPanel({ open, onClose, onCompleted }) {
  const { request } = useAuth();
  const [etapa, setEtapa] = useState(ETAPA_QR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [secret, setSecret] = useState("");
  const [qrDataUri, setQrDataUri] = useState("");
  const [codigo, setCodigo] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [salvouRecovery, setSalvouRecovery] = useState(false);
  // Sprint 9 — indica que MFA esta indisponivel no servidor (env var ausente).
  // Quando setado, a UI mostra mensagem clara e bloqueia setup-secret.
  const [mfaIndisponivelMotivo, setMfaIndisponivelMotivo] = useState(null);

  // Inicializa enrollment ao abrir.
  React.useEffect(() => {
    if (!open) {
      // reset ao fechar
      setEtapa(ETAPA_QR);
      setError(null);
      setSecret("");
      setQrDataUri("");
      setCodigo("");
      setRecoveryCodes([]);
      setSalvouRecovery(false);
      setMfaIndisponivelMotivo(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        // Sprint 9 — pre-check de saude. Evita o usuario sofrer um 503 no
        // setup-secret sem entender o motivo. Se MFA indisponivel, para aqui.
        const saudeResp = await fetchMfaSaude(request);
        const saude = saudeResp?.data || saudeResp;
        if (!saude?.disponivel) {
          setMfaIndisponivelMotivo(
            saude?.motivo ||
              "A autenticação em duas etapas não está disponível neste servidor.",
          );
          setLoading(false);
          return;
        }
        const resp = await postMfaSetupSecret(request);
        const data = resp?.data || resp;
        setSecret(data.secret_base32);
        setQrDataUri(data.qr_code_data_uri);
      } catch (err) {
        // Defesa em profundidade: se o setup-secret retornar 503 com codigo
        // `mfa_nao_configurado`, tratamos como indisponibilidade (UX clara)
        // em vez de mensagem generica.
        if (err?.codigo === "mfa_nao_configurado") {
          setMfaIndisponivelMotivo(
            err?.message || "MFA não está configurado neste servidor.",
          );
        } else {
          setError(err?.message || "Falha ao iniciar enrollment.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, request]);

  const verificar = async () => {
    setError(null);
    setLoading(true);
    try {
      const resp = await postMfaVerifyAndEnable(request, codigo);
      const data = resp?.data || resp;
      setRecoveryCodes(data.recovery_codes || []);
      setEtapa(ETAPA_RECOVERY);
    } catch (err) {
      setError(err?.message || "Código inválido.");
    } finally {
      setLoading(false);
    }
  };

  const finalizar = () => {
    onCompleted?.();
    onClose?.();
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Configurar MFA (autenticador)"
      subtitle="Adiciona um segundo fator de autenticação à sua conta."
      width="min(95vw, 520px)"
      footer={
        mfaIndisponivelMotivo ? (
          <SecondaryBtn type="button" onClick={onClose}>
            Fechar
          </SecondaryBtn>
        ) : etapa === ETAPA_RECOVERY ? (
          <>
            <SecondaryBtn type="button" onClick={onClose}>
              Sair sem confirmar
            </SecondaryBtn>
            <PrimaryBtn
              type="button"
              disabled={!salvouRecovery}
              onClick={finalizar}
            >
              <FiCheckCircle size={14} /> Concluir
            </PrimaryBtn>
          </>
        ) : etapa === ETAPA_VERIFICAR ? (
          <>
            <SecondaryBtn type="button" onClick={() => setEtapa(ETAPA_QR)}>
              Voltar
            </SecondaryBtn>
            <PrimaryBtn
              type="button"
              disabled={loading || codigo.length < 6}
              onClick={verificar}
            >
              <FiCheckCircle size={14} /> Verificar código
            </PrimaryBtn>
          </>
        ) : (
          <>
            <SecondaryBtn type="button" onClick={onClose}>
              Cancelar
            </SecondaryBtn>
            <PrimaryBtn
              type="button"
              disabled={loading || !qrDataUri}
              onClick={() => setEtapa(ETAPA_VERIFICAR)}
            >
              Já escaneei →
            </PrimaryBtn>
          </>
        )
      }
    >
      {mfaIndisponivelMotivo ? (
        <Section>
          <Warning>
            <FiAlertTriangle size={18} />
            <span>
              A autenticação em duas etapas <strong>não está disponível</strong>{" "}
              neste servidor no momento.
              <br />
              Procure o administrador do sistema para conclusão da configuração
              (<code>MFA_ENCRYPTION_KEY</code> precisa estar definida).
            </span>
          </Warning>
          <p style={{ fontSize: 12, opacity: 0.65, margin: 0 }}>
            Detalhes técnicos: {mfaIndisponivelMotivo}
          </p>
        </Section>
      ) : (
        <>
          {error && <ErrorMsg>{error}</ErrorMsg>}

          {etapa === ETAPA_QR && (
        <Section>
          <Step>Passo 1 de 3 · Escaneie o QR code</Step>
          <p>
            Abra seu app autenticador (Google Authenticator, Authy, 1Password)
            e escaneie o código abaixo.
          </p>
          {loading && <p>Carregando QR...</p>}
          {qrDataUri && <QrImg src={qrDataUri} alt="QR code do MFA" />}
          {secret && (
            <>
              <p style={{ fontSize: 12, opacity: 0.75 }}>
                Não consegue escanear? Digite o código manualmente:
              </p>
              <SecretBox>{secret}</SecretBox>
              <SecondaryBtn
                type="button"
                onClick={() => navigator.clipboard?.writeText(secret)}
              >
                <FiCopy size={14} /> Copiar secret
              </SecondaryBtn>
            </>
          )}
        </Section>
      )}

      {etapa === ETAPA_VERIFICAR && (
        <Section>
          <Step>Passo 2 de 3 · Confirme o código gerado</Step>
          <p>Digite o código de 6 dígitos que aparece no seu app:</p>
          <CodeInput
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
            // Sem maxLength: ele cortaria "123 456" colado do autenticador
            // antes da limpeza; o limite de 6 dígitos vem do slice acima.
            onPaste={(e) => {
              const colado = e.clipboardData?.getData("text");
              if (!colado) return;
              e.preventDefault();
              setCodigo(colado.replace(/\D/g, "").slice(0, 6));
            }}
            aria-label="Código TOTP de 6 dígitos"
          />
        </Section>
      )}

      {etapa === ETAPA_RECOVERY && (
        <Section>
          <Step>Passo 3 de 3 · Guarde os códigos de recuperação</Step>
          <Warning>
            <FiAlertTriangle size={18} />
            <span>
              Salve estes <strong>8 códigos de recuperação</strong> em local
              seguro. Cada um pode ser usado <strong>uma única vez</strong> se
              você perder acesso ao seu autenticador. <em>Não serão exibidos
              novamente.</em>
            </span>
          </Warning>
          <RecoveryList>
            {recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </RecoveryList>
          <SecondaryBtn
            type="button"
            onClick={() => navigator.clipboard?.writeText(recoveryCodes.join("\n"))}
          >
            <FiCopy size={14} /> Copiar todos
          </SecondaryBtn>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.9rem",
            }}
          >
            <input
              type="checkbox"
              checked={salvouRecovery}
              onChange={(e) => setSalvouRecovery(e.target.checked)}
            />
            Confirmo que salvei os códigos em local seguro.
          </label>
        </Section>
      )}
        </>
      )}
    </SlideOverPanel>
  );
}

export default MfaEnrollmentPanel;
